export function normalizeAltaRapidaDni(value) {
  return String(value || '').replace(/\D/g, '').replace(/^0+/, '');
}

export function formatAltaRapidaDniDisplay(dni) {
  const digits = normalizeAltaRapidaDni(dni);
  if (!digits) return '—';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function formatMedicamentoOptionLabel(m) {
  const clean = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const normalized = s.toLowerCase();
    if (normalized === 'null' || normalized === 'undefined' || normalized === '0' || normalized === '0.0') return '';
    return s;
  };
  const nombreGenerico = clean(m?.nombre_generico) || 'Sin nombre';
  const nombreComercial = clean(m?.nombre_comercial);
  const concentracion = clean(m?.concentracion);
  const laboratorio = clean(m?.laboratorio);
  const presentacion = clean(m?.presentacion);
  const detalles = [concentracion, presentacion, laboratorio].filter(Boolean).join(' · ');
  return `${nombreGenerico}${nombreComercial ? ` (${nombreComercial})` : ''}${detalles ? ` — ${detalles}` : ''}`;
}

export function buildPacienteNombreFromClienteRow(row) {
  if (!row) return null;
  const nombreGeclisa = String(row.nombre_geclisa || '').trim();
  const apellidoGeclisa = String(row.apellido_geclisa || '').trim();
  if (nombreGeclisa) {
    return `${nombreGeclisa}${apellidoGeclisa ? ` ${apellidoGeclisa}` : ''}`.trim();
  }
  const plex = String(row.nombre_plex || row.nombre_completo || '').trim();
  return plex || null;
}

export function mapClienteLookupToPaciente(row, source = 'geclisa') {
  if (!row) return null;
  const dni = normalizeAltaRapidaDni(row.dni_normalizado || row.dni);
  const paciente = buildPacienteNombreFromClienteRow(row);
  if (!dni && !paciente) return null;
  return {
    dni,
    paciente: paciente || `Paciente DNI ${formatAltaRapidaDniDisplay(dni)}`,
    obra_social: row.obra_social_descripcio || row.obra_social || row.os_sigla_geclisa || '',
    source,
    codcliente: row.codcliente ?? null,
    prestador: row.prestador_geclisa || null
  };
}

export function resolvePacienteFromHistorial(historialPacientes, dniRaw) {
  const dni = normalizeAltaRapidaDni(dniRaw);
  if (!dni) return null;
  const hit = (historialPacientes || []).find((p) => normalizeAltaRapidaDni(p.dni) === dni);
  if (!hit) return null;
  return {
    dni,
    paciente: hit.paciente || 'Paciente',
    obra_social: hit.obra_social || '',
    source: 'historial'
  };
}

export function mapClienteExternoToPaciente(row) {
  if (!row) return null;
  const dni = normalizeAltaRapidaDni(row.dni_normalizado || row.dni);
  const paciente = String(row.nombre_completo || '').trim();
  if (!dni && !paciente) return null;
  return {
    dni,
    paciente: paciente || `Paciente DNI ${formatAltaRapidaDniDisplay(dni)}`,
    obra_social: row.obra_social || '',
    source: 'externo',
    prestador: null,
    cliente_externo_id: row.id ?? null
  };
}

export async function crearClienteExternoAltaRapida(payload, apiBaseUrl) {
  const res = await fetch(`${apiBaseUrl}/api/asociacion-recetas/clientes-externos`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409) {
    const err = new Error(json.error || 'Ya existe un cliente con ese DNI');
    err.code = 'DUPLICATE_DNI';
    throw err;
  }
  if (!res.ok) {
    throw new Error(json.message || json.error || `HTTP ${res.status}`);
  }
  return json.data || null;
}

export function mapAltaRapidaPedidoResponse(nuevoRaw, medicamentoId, selectedMed) {
  const medLabel = selectedMed
    ? [selectedMed.nombre_generico, selectedMed.nombre_comercial ? `(${selectedMed.nombre_comercial})` : '', selectedMed.concentracion || '']
      .filter(Boolean)
      .join(' ')
    : '';
  return {
    id: nuevoRaw.id,
    solicitante_dni: nuevoRaw.solicitante_dni,
    paciente_dni: nuevoRaw.paciente_dni,
    paciente: nuevoRaw.paciente_nombre || nuevoRaw.paciente_dni || nuevoRaw.solicitante_dni || 'Paciente',
    dni: nuevoRaw.paciente_dni || nuevoRaw.solicitante_dni || '',
    medicamento: medLabel,
    obra_social: nuevoRaw.obra_social_descripcio || nuevoRaw.obra_social || nuevoRaw.os_sigla_geclisa || '',
    medicamento_generico: selectedMed?.nombre_generico || null,
    medicamento_concentracion: selectedMed?.concentracion || '',
    medicamento_laboratorio: selectedMed?.laboratorio || '',
    nombre_comercial: selectedMed?.nombre_comercial || '',
    medicamento_id: medicamentoId,
    solicitado: nuevoRaw.fecha_solicitud || null,
    fecha_solicitud: nuevoRaw.fecha_solicitud,
    estado: nuevoRaw.estado || 'pendiente_aprobacion_medica',
    acepta_recordatorios: !!nuevoRaw.acepta_recordatorios,
    motivo_rechazo: nuevoRaw.motivo_rechazo || null,
    notas: nuevoRaw.notas || '',
    medico_id: nuevoRaw.medico_id || null,
    medico: nuevoRaw.medico || nuevoRaw.prestador_geclisa || '',
    prestador_geclisa: nuevoRaw.prestador_geclisa || '',
    cantidad_recetas: nuevoRaw.cantidad_recetas || 0,
    recetas_generadas: nuevoRaw.recetas_generadas ?? 0,
    posologia: nuevoRaw.posologia || '',
    es_pami: nuevoRaw.es_pami === 1 || nuevoRaw.es_pami === true
  };
}
